/**
 * Hub — the responsive home screen.
 *
 * Mirrors the Android `HomeScreen.kt` layout (favourites carousels split
 * by kind + a single "Continue watching" strip + hub-tile grid +
 * footer). Per spec §2 the rails share one carousel component
 * (`HubCarousel`) parameterised by data source rather than three near-
 * copies of the same scroll logic.
 *
 * Continue-watching click routes:
 *   vod      → /watch/vod/{id}                                    (direct)
 *   series   → call Episodes.resolve(...) → /watch/episode/{eid}  (deep-link)
 *   catchup  → /watch/catchup/{cid}?start_ms=&duration=&resume=N  (deep-link)
 *
 * Spec: docs/superpowers/specs/2026-05-10-hub-overlay-and-deep-links.md.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  History as HistoryIcon,
  Radio,
  X,
} from "lucide-react";
import PageLayout from "../components/PageLayout";
import { EmptyState } from "../components/EmptyState";
import { PosterImage } from "../components/PosterImage";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { Catchup } from "../lib/catchup";
import { Episodes } from "../lib/episodes";
import { db, type FavoriteRow } from "../lib/db";
import { Favorites, syncFavoritesCache } from "../lib/favorites";
import { pickLiveNow, pickRecentlyAdded } from "../lib/hubRails";
import { LiveNowCard } from "../components/LiveNowCard";
import { StreamCard } from "../components/StreamCard";
import { Resume, syncResumeCache, type ServerResume } from "../lib/resume";

type ContinueRow = ServerResume & { progress: number };

export default function HubPage() {
  const { user } = useAuth();
  const { activePlaylist, catalog } = useCatalog();
  const navigate = useNavigate();
  const [favs, setFavs] = useState<FavoriteRow[]>([]);
  const [resumes, setResumes] = useState<ContinueRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refreshFromServer = useCallback(async () => {
    if (!user || !activePlaylist) return;
    try {
      const [favList, resumeList] = await Promise.all([
        Favorites.list(activePlaylist.id),
        Resume.list(activePlaylist.id),
      ]);
      await syncFavoritesCache(user.id, activePlaylist.id, favList);
      await syncResumeCache(user.id, activePlaylist.id, resumeList);
      const favRows = await db.favorites
        .where("playlistId").equals(activePlaylist.id).toArray();
      favRows.sort((a, b) => {
        const ai = a.sortIndex ?? Number.MAX_SAFE_INTEGER;
        const bi = b.sortIndex ?? Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a.addedAt ?? 0) - (b.addedAt ?? 0);
      });
      setFavs(favRows);
      setResumes(resumeList.map((r) => ({
        ...r,
        progress: r.duration_ms > 0 ? r.position_ms / r.duration_ms : 0,
      })));
      setError(null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (e as { message?: string })?.message
        || "Couldn't sync with the server";
      setError(msg);
    }
  }, [user, activePlaylist]);

  useEffect(() => {
    if (!user || !activePlaylist) return;
    // Cache-first paint, then reconcile with the server.
    (async () => {
      const [favRows, resumeRows] = await Promise.all([
        db.favorites.where("playlistId").equals(activePlaylist.id).toArray(),
        db.resume.where("playlistId").equals(activePlaylist.id).toArray(),
      ]);
      favRows.sort((a, b) => {
        const ai = a.sortIndex ?? Number.MAX_SAFE_INTEGER;
        const bi = b.sortIndex ?? Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a.addedAt ?? 0) - (b.addedAt ?? 0);
      });
      setFavs(favRows);
      resumeRows.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      setResumes(resumeRows.map((r) => ({
        id: 0,
        stream_kind: r.streamKind,
        stream_id: r.streamId,
        title: r.title,
        poster: r.poster ?? null,
        position_ms: Math.floor(r.position * 1000),
        duration_ms: Math.floor(r.duration * 1000),
        series_id: r.seriesId ?? null,
        catchup_channel_id: r.catchupChannelId ?? null,
        catchup_start_ms: r.catchupStartMs ?? null,
        catchup_duration_min: r.catchupDurationMin ?? null,
        catchup_channel_name: r.catchupChannelName ?? null,
        catchup_programme_title: r.catchupProgrammeTitle ?? null,
        updated_at: new Date(r.updatedAt || Date.now()).toISOString(),
        progress: r.duration > 0 ? r.position / r.duration : 0,
      })));
    })();
    refreshFromServer();
  }, [user, activePlaylist, refreshFromServer]);

  // Re-sync when the page regains focus — catches favs/progress added on
  // another device or in a different tab.
  useEffect(() => {
    const onFocus = () => refreshFromServer();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshFromServer]);

  const favSections = useMemo(() => {
    const live: FavoriteRow[] = [];
    const vod: FavoriteRow[] = [];
    const series: FavoriteRow[] = [];
    for (const f of favs) {
      if (f.streamKind === "live" || f.streamKind === "catchup") live.push(f);
      else if (f.streamKind === "vod") vod.push(f);
      else if (f.streamKind === "series") series.push(f);
    }
    return { live, vod, series };
  }, [favs]);

  const liveNow = useMemo(
    () => pickLiveNow(catalog?.live?.streams, favs.filter((f) => f.streamKind === "live").map((f) => String(f.streamId))),
    [catalog, favs],
  );
  const recentlyAdded = useMemo(() => pickRecentlyAdded(catalog?.vod?.streams), [catalog]);

  const removeResume = async (r: ContinueRow) => {
    if (!activePlaylist || !user) return;
    setResumes((prev) => prev.filter((x) =>
      !(x.stream_kind === r.stream_kind && x.stream_id === r.stream_id)));
    const key = `${user.id}:${activePlaylist.id}:${r.stream_kind}:${r.stream_id}`;
    await db.resume.delete(key);
    try {
      await Resume.remove(activePlaylist.id, r.stream_kind, r.stream_id);
    } catch {
      /* best-effort — local cache already updated */
    }
  };

  // Click handler for a continue-watching card. For series + catchup we
  // re-resolve the URL via the backend; for vod we navigate straight in.
  const onContinueClick = async (r: ContinueRow) => {
    if (!activePlaylist) return;
    if (r.stream_kind === "vod") {
      navigate(`/watch/vod/${encodeURIComponent(r.stream_id)}`);
      return;
    }
    if (r.stream_kind === "series" && r.series_id) {
      try {
        const ep = await Episodes.resolve(
          activePlaylist.id, r.series_id, r.stream_id,
        );
        navigate(
          `/watch/episode/${encodeURIComponent(r.stream_id)}?series=${encodeURIComponent(r.series_id)}&season=${encodeURIComponent(String(ep.season ?? ""))}`,
          {
            state: {
              episode: {
                id: r.stream_id,
                season: String(ep.season ?? ""),
                episode_num: ep.episode_num ?? "",
                title: ep.title,
                container: ep.container ?? "mkv",
                duration: ep.duration_secs ?? null,
                still: ep.poster ?? null,
                url: ep.direct_url,
                proxy_url: ep.proxy_url,
              },
              seriesName: ep.parent_title,
            },
          },
        );
      } catch {
        // Resolve failed (panel down, episode gone, etc) — fall back to
        // the series detail page so the user has a path forward.
        navigate(`/series/${encodeURIComponent(r.series_id)}`);
      }
      return;
    }
    if (
      r.stream_kind === "catchup" &&
      r.catchup_channel_id &&
      r.catchup_start_ms !== null &&
      r.catchup_duration_min !== null
    ) {
      // Resume value (seconds) survives via the `resume` query param —
      // WatchPage reads it and calls setSeekTime so the catchup URL is
      // built with the correct shifted start. Server resolve gives us a
      // fresh URL but we only need its existence; the URL is derived in
      // WatchPage from the same params via /catchup-url.
      const resumeSec = Math.floor(r.position_ms / 1000);
      try {
        await Catchup.resolve(
          activePlaylist.id,
          r.catchup_channel_id,
          r.catchup_start_ms,
          r.catchup_duration_min,
        );
      } catch {
        /* best-effort — WatchPage will re-resolve via /catchup-url */
      }
      const params = new URLSearchParams({
        start_ms: String(r.catchup_start_ms),
        duration: String(r.catchup_duration_min),
        resume: String(resumeSec),
      });
      navigate(
        `/watch/catchup/${encodeURIComponent(r.catchup_channel_id)}?${params.toString()}`,
        {
          state: {
            catchupProgram: {
              title: r.catchup_programme_title || r.title,
              startMs: r.catchup_start_ms,
              endMs:
                r.catchup_start_ms + (r.catchup_duration_min * 60_000),
            },
          },
        },
      );
      return;
    }
    // No metadata → stay where we are. (Live/series rows without enough
    // info to re-hydrate.)
  };

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState title="Pick a playlist" cta={{ to: "/settings", label: "Open settings" }} />
      </PageLayout>
    );
  }

  const hasAny =
    favSections.live.length > 0 ||
    favSections.vod.length > 0 ||
    favSections.series.length > 0 ||
    resumes.length > 0;

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4 space-y-8">
        {error && (
          <p className="text-[12px] font-ui text-danger">
            Couldn't sync with the server: {error}. Showing the local cache.
          </p>
        )}

        {!hasAny && (
          <EmptyState
            title="Your hub is empty"
            hint="Mark live channels, movies, and series as favourites — and they'll show up here. Anything you start watching also appears under Continue watching."
            cta={{ to: "/livetv", label: "Browse Live TV" }}
          />
        )}

        {resumes.length > 0 && (
          <HubGrid title="Continue watching" layout="landscape">
            {resumes.map((r) => (
              <ResumeCard
                key={`${r.stream_kind}:${r.stream_id}`}
                r={r}
                onClick={() => onContinueClick(r)}
                onRemove={() => removeResume(r)}
              />
            ))}
          </HubGrid>
        )}

        {liveNow.length > 0 && activePlaylist && (
          <HubGrid title="Live now" layout="landscape">
            {liveNow.map((c) => (
              <LiveNowCard key={c.id} stream={c} pid={activePlaylist.id} />
            ))}
          </HubGrid>
        )}

        {recentlyAdded.length > 0 && (
          <HubGrid title="Recently added films" layout="portrait">
            {recentlyAdded.map((m) => (
              <StreamCard key={m.id} stream={m} layout="portrait" />
            ))}
          </HubGrid>
        )}

        {favSections.live.length > 0 && (
          <HubGrid title="Favourites — Live TV" layout="landscape">
            {favSections.live.map((f) => (
              <FavCard key={f.key} f={f} layout="landscape" />
            ))}
          </HubGrid>
        )}
        {favSections.vod.length > 0 && (
          <HubGrid title="Favourites — Movies" layout="portrait">
            {favSections.vod.map((f) => (
              <FavCard key={f.key} f={f} layout="portrait" />
            ))}
          </HubGrid>
        )}
        {favSections.series.length > 0 && (
          <HubGrid title="Favourites — Series" layout="portrait">
            {favSections.series.map((f) => (
              <FavCard key={f.key} f={f} layout="portrait" />
            ))}
          </HubGrid>
        )}
      </div>
    </PageLayout>
  );
}

// ─── Grid ──────────────────────────────────────────────────────────────
// Replaces the old horizontal carousel. Cards wrap onto multiple rows so
// the whole hub becomes one tall vertical-scroll page (per user request:
// "1 big page of our content showing, divided though"). Column counts are
// tuned per orientation — portrait cards (vod/series) are narrower so
// they fit more per row than landscape (live/continue) cards.
function HubGrid({
  title,
  layout,
  children,
}: {
  title: string;
  layout: "landscape" | "portrait";
  children: React.ReactNode;
}) {
  const gridCols = layout === "landscape"
    ? "sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
    : "sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8";
  // Phone is a horizontal rail with the next item peeking, so the row reads as
  // scrollable without a control. Tablet and up stay a grid — a rail wastes the
  // width there and hides most of the content behind a scroll.
  const railItem = layout === "landscape"
    ? "[&>*]:w-[68vw] [&>*]:max-w-[260px]"
    : "[&>*]:w-[38vw] [&>*]:max-w-[150px]";
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="font-data font-semibold text-[11px] uppercase tracking-[0.13em] text-text-secondary">
          {title}
        </h2>
      </div>
      <div
        className={`flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-pl-4 -mx-4 px-4 pb-1 [&>*]:flex-shrink-0 [&>*]:snap-start ${railItem} sm:mx-0 sm:px-0 sm:scroll-pl-0 sm:grid sm:overflow-visible sm:[&>*]:w-auto sm:[&>*]:max-w-none ${gridCols}`}
      >
        {children}
      </div>
    </section>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────────
function FavCard({ f, layout }: { f: FavoriteRow; layout: "landscape" | "portrait" }) {
  // Live + catchup combined favs need the chooser; everything else is a
  // direct link. Re-use a tiny inline chooser since it's only two options.
  const [showChooser, setShowChooser] = useState(false);
  const isCombined = f.streamKind === "live" && f.hasCatchup && !!f.catchupStreamId;
  const aspectCls = layout === "landscape" ? "aspect-video" : "aspect-[2/3]";
  const fitCls = layout === "landscape" ? "object-contain p-3" : "object-cover";
  const liveHref = `/watch/live/${encodeURIComponent(f.streamId)}`;
  const catchupHref = isCombined
    ? `/catchup/${encodeURIComponent(f.catchupStreamId!)}`
    : null;
  const otherHref = f.streamKind === "series"
    ? `/series/${encodeURIComponent(f.streamId)}`
    : `/watch/${f.streamKind}/${encodeURIComponent(f.streamId)}`;
  const onClick = (e: React.MouseEvent) => {
    if (!isCombined) return;
    e.preventDefault();
    setShowChooser(true);
  };
  return (
    <div className="relative group">
      <Link to={isCombined ? liveHref : otherHref} onClick={onClick} className="block">
        <div className={`relative ${aspectCls} rounded-lg overflow-hidden bg-canvas border border-muted/30 transition-fast group-hover:border-accent-gold/60`}>
          <PosterImage
            src={f.poster}
            alt={f.title}
            className="w-full h-full"
            imgClass={`w-full h-full ${fitCls}`}
            orientation={layout === "landscape" ? "row" : "stack"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent opacity-60 group-hover:opacity-90 transition-fast" />
          {f.streamKind === "live" && f.hasCatchup && (
            <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-pill bg-accent-gold/90 text-canvas text-[9px] font-ui font-bold uppercase tracking-[0.06em]">
              + Catchup
            </div>
          )}
        </div>
        <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
          {f.title}
        </p>
      </Link>
      {showChooser && isCombined && catchupHref && (
        <div
          className="fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowChooser(false)}
        >
          <div
            className="glass-panel rounded-2xl p-5 w-full max-w-sm space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-[22px] tracking-[-0.5px] text-text-primary">
                {f.title}
              </h3>
              <button
                type="button"
                onClick={() => setShowChooser(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-card-hover text-text-secondary"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <Link
              to={liveHref}
              onClick={() => setShowChooser(false)}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface hover:bg-card-hover border border-muted/30 hover:border-accent-gold/60 transition-fast"
            >
              <span className="w-12 h-12 rounded-full bg-danger/20 text-danger flex items-center justify-center flex-shrink-0">
                <Radio size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-ui font-medium text-text-primary">Watch live</span>
                <span className="block text-[12px] font-ui text-text-secondary">Stream the channel right now</span>
              </span>
            </Link>
            <Link
              to={catchupHref}
              onClick={() => setShowChooser(false)}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface hover:bg-card-hover border border-muted/30 hover:border-accent-gold/60 transition-fast"
            >
              <span className="w-12 h-12 rounded-full bg-accent-gold/20 text-accent-gold flex items-center justify-center flex-shrink-0">
                <HistoryIcon size={22} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-ui font-medium text-text-primary">Catch up</span>
                <span className="block text-[12px] font-ui text-text-secondary">Pick a programme that already aired</span>
              </span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function ResumeCard({
  r,
  onClick,
  onRemove,
}: {
  r: ContinueRow;
  onClick: () => void;
  onRemove: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const subtitle = (() => {
    if (r.stream_kind === "catchup" && r.catchup_channel_name) {
      return r.catchup_channel_name;
    }
    if (r.stream_kind === "series") return "Episode";
    if (r.stream_kind === "vod") return "Movie";
    return "";
  })();
  const pct = Math.max(0, Math.min(100, r.progress * 100));
  return (
    <div
      className="relative group"
      onContextMenu={(e) => { e.preventDefault(); setMenu(true); }}
    >
      <button
        type="button"
        onClick={onClick}
        className="block w-full text-left"
      >
        <div className="relative aspect-video rounded-lg overflow-hidden bg-canvas border border-muted/30 transition-fast group-hover:border-accent-gold/60">
          <PosterImage
            src={r.poster}
            alt={r.title}
            className="w-full h-full"
            imgClass="w-full h-full object-cover"
            orientation="row"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent opacity-60 group-hover:opacity-90 transition-fast" />
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-line">
            <div className="h-full bg-accent-gold" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
          {r.title}
        </p>
        {subtitle && (
          <p className="text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary truncate">
            {subtitle}
          </p>
        )}
      </button>
      {/* "···" remove button — visible on hover (desktop) and always on
          touch devices via long-press → onContextMenu. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenu(true); }}
        className="absolute top-1.5 right-1.5 w-9 h-9 flex items-center justify-center rounded-full bg-canvas/80 backdrop-blur-sm text-text-secondary opacity-0 group-hover:opacity-90 hover:opacity-100 hover:text-accent-gold transition-fast z-10"
        aria-label="Card options"
      >
        <span className="text-[18px] leading-none">…</span>
      </button>
      {menu && (
        <div
          className="fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setMenu(false)}
        >
          <div
            className="glass-panel rounded-2xl p-4 w-full max-w-xs space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-ui font-medium text-[13px] text-text-primary mb-1 truncate">
              {r.title}
            </p>
            <button
              type="button"
              onClick={() => { setMenu(false); onRemove(); }}
              className="w-full text-left p-3 rounded-xl bg-surface hover:bg-card-hover border border-muted/30 transition-fast text-[13px] font-ui font-medium text-text-primary"
            >
              Remove from Continue Watching
            </button>
            <button
              type="button"
              onClick={() => setMenu(false)}
              className="w-full text-left p-3 rounded-xl text-[12px] font-ui text-text-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

